import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, CheckCircle, ArrowRight, Shield } from "lucide-react";

interface ComplianceWidgetProps {
  activeRulesCount: number;
}

const ComplianceWidget: React.FC<ComplianceWidgetProps> = ({
  activeRulesCount,
}) => {
  const navigate = useNavigate();

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck
          className="h-5 w-5 text-slate-700"
          aria-hidden="true"
        />
        <h2 className="text-lg font-bold" style={{ color: "#1F2937" }}>
          Billing Compliance
        </h2>
      </div>

      {/* Body */}
      {activeRulesCount === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
            <Shield className="h-6 w-6 text-slate-300" />
          </div>
          <p className="font-medium text-slate-500 text-sm">
            No billing rules configured
          </p>
          <p className="text-xs text-slate-400 mb-3">
            Set up rules to enforce billing compliance
          </p>
          <button
            type="button"
            onClick={() => navigate("/billing")}
            className="text-xs font-semibold transition-colors"
            style={{ color: "var(--color-primary)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--color-primary-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--color-primary)";
            }}
          >
            Set up rules
          </button>
        </div>
      ) : (
        <>
          {/* Active rules card */}
          <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-lg mb-4">
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "var(--color-primary-light)" }}
            >
              <Shield
                className="h-4.5 w-4.5"
                style={{ color: "var(--color-accent)" }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-sm font-bold"
                style={{ color: "#1F2937" }}
              >
                {activeRulesCount} active rule{activeRulesCount !== 1 ? "s" : ""}
              </p>
              <p className="text-xs text-slate-500">
                Billing compliance enforced
              </p>
            </div>
            <CheckCircle
              className="h-5 w-5 text-emerald-500 flex-shrink-0"
              aria-hidden="true"
            />
          </div>

          {/* Footer */}
          <div className="pt-1">
            <button
              type="button"
              onClick={() => navigate("/billing")}
              className="flex items-center gap-1.5 text-sm font-semibold transition-colors"
              style={{ color: "var(--color-primary)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--color-primary-hover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--color-primary)";
              }}
            >
              Manage Rules
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </>
      )}
    </motion.section>
  );
};

export default ComplianceWidget;
